import type { RoundSnapshot } from "../../domain/round/round-reducer.js";
import { roundDate } from "../../domain/round/round-date.js";
import {
  isRoundV3,
  type RoundConditionTag,
} from "../../domain/round/round-types.js";
import { selectPlayerSummary } from "../../domain/round/round-selectors.js";
import type { RoundListItem } from "../../shared/contracts/product-pages.js";

export interface PlayerView {
  id: string;
  name: string;
  gross: number | null;
  net: number | null;
  stableford: number | null;
  playingHandicap: number | null;
  winner: boolean;
  isSelf: boolean;
  holes: Array<number | null>;
  /** Conditions the player tagged at tee off; null when none were set. */
  tags: RoundConditionTag[] | null;
}

export type ScoreMark =
  "albatross" | "eagle" | "birdie" | "par" | "bogey" | "double";

export function scoreMark(
  strokes: number | null,
  par: number,
): ScoreMark | null {
  if (strokes === null) return null;
  const diff = strokes - par;
  if (diff <= -3) return "albatross";
  if (diff === -2) return "eagle";
  if (diff === -1) return "birdie";
  if (diff === 0) return "par";
  if (diff === 1) return "bogey";
  return "double";
}

/** Sum of the given hole numbers for one player; null when nothing is set. */
export function sumHoles(
  holes: Array<number | null>,
  from: number,
  to: number,
): number | null {
  let total = 0;
  let any = false;
  for (let index = from; index < to && index < holes.length; index += 1) {
    const value = holes[index];
    if (value === null || value === undefined) continue;
    total += value;
    any = true;
  }
  return any ? total : null;
}

/**
 * Sum of holes `from` (inclusive) to `to` (exclusive), or null unless every
 * one of those holes has a score. Used for "best front/back nine".
 */
export function completeSum(
  holes: Array<number | null>,
  from: number,
  to: number,
): number | null {
  if (to > holes.length) return null;
  let total = 0;
  for (let index = from; index < to; index += 1) {
    const value = holes[index];
    if (value === null || value === undefined) return null;
    total += value;
  }
  return total;
}

/**
 * Running score to par for one participant: gross of the scored holes minus
 * the par of those same holes. Null when nothing has been scored yet.
 */
export function scoreToPar(
  snapshot: RoundSnapshot,
  participantId: string,
): number | null {
  if (isRoundV3(snapshot))
    return selectPlayerSummary(snapshot, participantId).vsCoursePar;
  const scores = snapshot.scores[participantId] ?? {};
  let diff = 0;
  let any = false;
  for (const hole of snapshot.course.holes) {
    const strokes = scores[hole.number];
    if (strokes === null || strokes === undefined) continue;
    diff += strokes - hole.par;
    any = true;
  }
  return any ? diff : null;
}

/** "12.4" style handicap text, or null when the value is unknown. */
export function formatHandicap(
  value: number | null | undefined,
): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const text = value.toFixed(1);
  return (text === "-0.0" ? "0.0" : text).replace("-", "−");
}

export function playersOf(
  snapshot: RoundSnapshot,
  selfName: string,
): PlayerView[] {
  const winners = new Set(
    snapshot.result?.winnerIds ?? snapshot.declaredWinnerIds,
  );
  return snapshot.participants.map((participant) => {
    const standing = snapshot.result?.standings.find(
      (row) => row.targetId === participant.id,
    );
    const scores = snapshot.scores[participant.id] ?? {};
    const holes = snapshot.course.holes.map(
      (hole) => scores[hole.number] ?? null,
    );
    const summary = isRoundV3(snapshot)
      ? selectPlayerSummary(snapshot, participant.id)
      : null;
    const imported = snapshot.importedTotals?.players[participant.id];
    const gross = imported
      ? imported.gross
      : summary
        ? summary.actualStrokes
        : (standing?.gross ?? sumHoles(holes, 0, holes.length));
    return {
      id: participant.id,
      name: participant.displayName,
      gross,
      net: imported
        ? imported.net
        : summary
          ? summary.netStrokes
          : (standing?.net ??
            (gross === null || participant.playingHandicap === null
              ? null
              : gross - participant.playingHandicap)),
      stableford: imported
        ? imported.stableford
        : summary
          ? snapshot.rules.handicapMode === "off"
            ? summary.grossStableford
            : summary.netStableford
          : (standing?.stableford ?? null),
      playingHandicap: participant.playingHandicap,
      winner: winners.has(participant.id),
      isSelf: participant.displayName === selfName,
      holes,
      tags: participant.tags?.length ? participant.tags : null,
    };
  });
}

/** The list-item view of a snapshot, for opening it in the round dialog. */
export function roundListItemOf(snapshot: RoundSnapshot): RoundListItem {
  return {
    id: snapshot.roundId,
    state: snapshot.state,
    courseName: snapshot.course.name,
    teeName: snapshot.course.teeName,
    format: snapshot.rules.format,
    completedAt: null,
    playedAt: snapshot.conditions.playedAt,
    date: roundDate(snapshot),
    participantNames: snapshot.participants.map((p) => p.displayName),
    gross: null,
    net: null,
    outcome: "none",
    stake: snapshot.rules.stake ?? null,
  };
}

export function hasHoleScores(snapshot: RoundSnapshot): boolean {
  return Object.values(snapshot.scores).some((scores) =>
    Object.values(scores).some(
      (value) => value !== null && value !== undefined,
    ),
  );
}

/** Headline from the viewer's perspective: Won / Lost / Halved. */
export function outcomeHeadline(players: PlayerView[], tied: boolean): string {
  const self = players.find((player) => player.isSelf);
  if (players.length < 2) return "Round complete";
  if (tied) return "Halved";
  if (!self) return "Round completed";
  return self.winner ? "Won" : "Lost";
}
